import { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { fr } from 'react-day-picker/locale';
import { LuCalendar } from 'react-icons/lu';

interface DatePickerProps {
  value?: Date | null;
  onChange?: (date: Date | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
}

export function DatePicker({
  value,
  onChange,
  disabled,
  placeholder = '__/__/____',
  minDate,
  maxDate,
  className,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const formattedDate = value
    ? new Intl.DateTimeFormat('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }).format(value)
    : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        disabled={disabled}
        className={cn(
          'h-12 rounded-1.5xl inline-flex w-full cursor-pointer items-center justify-start gap-2 border px-4 py-2 text-left font-medium disabled:pointer-events-none disabled:opacity-50',
          open
            ? 'border-indigo-900 bg-white'
            : value
            ? 'bg-blue-50'
            : 'border-gray-200 bg-white text-gray-400',
          className,
        )}
      >
        <LuCalendar className="size-5" />
        {formattedDate ?? placeholder}
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="center">
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={(date) => {
            onChange?.(date);
            setOpen(false);
          }}
          defaultMonth={value ?? undefined}
          locale={fr}
          weekStartsOn={1}
          disabled={
            minDate || maxDate
              ? (date) => {
                  if (minDate && date < minDate) return true;
                  if (maxDate && date > maxDate) return true;
                  return false;
                }
              : undefined
          }
        />
      </PopoverContent>
    </Popover>
  );
}
